<template>
  <v-container fluid>
    <v-card outlined>
      <v-card-text>
        <v-tabs v-model="activeTab">
          <!-- One save writes both sections, so the dot is what tells the admin which one is going
               with it — including the tab they are not looking at. -->
          <v-tab>
            {{ $t('documentDisplay.tab') }}
            <v-icon v-if="displayDirty" x-small color="primary" class="ml-1">mdi-circle</v-icon>
          </v-tab>
          <v-tab v-if="isOctipasAdmin">
            {{ $t('documentSettings.tab') }}
            <v-icon v-if="registryDirty" x-small color="primary" class="ml-1">mdi-circle</v-icon>
          </v-tab>
          <v-tab-item eager>
            <document-display-settings
              :settings="settings"
              :draft-fields="registryPayload"
              :busy="busy"
              :columns-error="columnsError"
              :committed="committed"
              :loading="loading"
              :load-error="loadError"
              @columns="columns = $event"
              @change="displayPayload = $event"
              @dirty="displayDirty = $event"
              @retry="load"
            />
          </v-tab-item>
          <v-tab-item v-if="isOctipasAdmin">
            <document-custom-fields
              :fields="settings && settings.custom_fields"
              :columns="columns"
              :busy="busy"
              :committed="committed"
              :loading="loading"
              :load-error="loadError"
              @change="registryPayload = $event"
              @dirty="registryDirty = $event"
              @retry="load"
            />
          </v-tab-item>
        </v-tabs>
        <v-alert v-if="saveError" type="error" dense text class="mt-4">{{ saveErrorMessage }}</v-alert>
        <div class="text-center mt-6 mb-2">
          <p v-if="hasDraft" class="text-body-2 warning--text mb-1">
            <v-icon x-small color="warning" aria-hidden="true">mdi-alert-circle-outline</v-icon>
            {{ $t('documentSettings.unsavedChanges') }}
          </p>
          <v-btn color="primary" outlined :loading="saving" :disabled="busy || !hasDraft" @click="save">
            {{ $t('documentSettings.save') }}
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
    <v-dialog v-model="leaveDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title style="justify-content: center">{{ $t('documentSettings.leaveTitle') }}</v-card-title>
        <v-card-text class="pb-0 text-center">{{ $t('documentSettings.leaveMessage') }}</v-card-text>
        <v-card-actions class="pa-0 mt-4">
          <v-btn color="primary" tile style="width: 50%" @click="stay">{{ $t('documentSettings.leaveStay') }}</v-btn>
          <v-btn color="secondary" class="ml-0" tile style="width: 50%" @click="leave">
            {{ $t('documentSettings.leaveDiscard') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script>
  import { mapState } from 'vuex'
  import DocumentDisplaySettings from '@/components/documents/DocumentDisplaySettings.vue'
  import DocumentCustomFields from '@/components/documents/DocumentCustomFields.vue'
  import { DocumentSettingsService } from '@/services/documentSettingsService'
  import { documentColumnsError } from '@/helpers/documentDisplay'
  import { EventBus } from '@/event-bus'
  export default {
    name: 'DocumentSettings',
    components: { DocumentDisplaySettings, DocumentCustomFields },
    // `columns` is the selection the display tab currently shows, not the saved one: the registry tab
    // refuses to deactivate a key that is selected there, and an unsaved selection must count too. The
    // display tab is eager, so it has always announced its columns before the registry tab is opened.
    data: () => ({
      activeTab: 0,
      settings: null,
      columns: [],
      // What each tab would write, kept here so one save can carry both — the api validates
      // list.columns against the custom_fields of the same body, which is how a key can be activated
      // and selected as a column in one go.
      displayPayload: null,
      registryPayload: null,
      displayDirty: false,
      registryDirty: false,
      // Bumped after a successful write so each tab moves its own baseline.
      committed: 0,
      saving: false,
      saveError: null,
      saveErrorFields: [],
      columnsError: '',
      leaveDialog: false,
      loading: true,
      loadError: false,
    }),
    computed: {
      ...mapState(['isOctipasAdmin']),
      busy() {
        return this.loading || this.saving || this.loadError
      },
      hasDraft() {
        return this.displayDirty || this.registryDirty
      },
      // Not the absence of custom_fields in the answer: it means either a role that may not see them,
      // or no document yet.
      registryAllowed() {
        return this.isOctipasAdmin
      },
      saveErrorMessage() {
        return this.saveError
          ? this.$t(`documentSettings.${this.saveError}`, { fields: this.saveErrorFields.join(', ') })
          : ''
      },
    },
    created() {
      this.service = new DocumentSettingsService(this.$auth)
      this.request = 0
      this.destroyed = false
      this.pendingLeave = null
      this.load()
      window.addEventListener('beforeunload', this.warnBeforeUnload)
    },
    // No refresh on window focus: with devtools open every click on the page refocuses the window,
    // so the screen reloaded on each one. What it used to guard — a key deactivated elsewhere while
    // the columns are being configured — is covered by the reload after each save, and by the api
    // naming the offending keys when a save finally conflicts.
    beforeDestroy() {
      this.destroyed = true
      this.request++
      window.removeEventListener('beforeunload', this.warnBeforeUnload)
    },
    // Leaving the screen is the one way a draft is lost for good, so it is the one place worth
    // asking. Navigating between the two tabs keeps both drafts alive and asks nothing.
    beforeRouteLeave(to, from, next) {
      if (!this.hasDraft) {
        next()
        return
      }
      this.pendingLeave = next
      this.leaveDialog = true
    },
    methods: {
      // Closing the tab or reloading never reaches the router, so the browser's own prompt covers it.
      // Its wording belongs to the browser; setting returnValue is what makes it appear at all.
      warnBeforeUnload(event) {
        if (this.hasDraft) {
          event.preventDefault()
          event.returnValue = ''
        }
      },
      stay() {
        this.leaveDialog = false
        // Cancelling the navigation, rather than dropping it: the address bar must go back to this
        // screen, which is where the admin still is.
        this.pendingLeave(false)
        this.pendingLeave = null
      },
      leave() {
        this.leaveDialog = false
        this.pendingLeave()
        this.pendingLeave = null
      },
      // Both tabs read the same document, so the view owns the call and hands each one its part.
      // Always fresh, never the drawer's cache: an editor must not present the admin with settings
      // older than the ones they are about to overwrite.
      async load() {
        const request = ++this.request
        this.loading = true
        this.loadError = false
        try {
          const settings = await this.service.getSettings()
          if (this.current(request)) {
            this.settings = settings
          }
        } catch {
          if (this.current(request)) {
            this.loadError = true
          }
        } finally {
          if (this.current(request)) {
            this.loading = false
          }
        }
      },
      // Only the sections that changed are sent: the api writes the keys the body carries and leaves
      // the rest as it stands, so an admin reordering columns never resends a registry they did not
      // touch — and never collides with a key a colleague added since the load.
      async save() {
        if (this.busy || !this.hasDraft) {
          return
        }
        // Hand-rolled rather than a vee-validate rule: the columns are a reorderable list driven by
        // buttons, not an input a ValidationProvider could wrap.
        this.columnsError = this.displayDirty ? documentColumnsError(this.displayPayload.list.columns) : ''
        if (this.columnsError) {
          this.activeTab = 0
          return
        }
        // Read before the write: committing clears both flags, and the drawer still has to be told
        // whether the menu configuration was part of it.
        const display = this.displayDirty
        const registry = this.registryDirty && this.registryAllowed
        // An empty body is not a no-op: the api upserts on it and bumps __v, so it would create a blank
        // document and report success, and could fail a concurrent editor with a 409.
        if (!display && !registry) {
          return
        }
        const body = {
          ...(display ? this.displayPayload : {}),
          ...(registry ? { custom_fields: this.registryPayload } : {}),
        }
        this.saving = true
        this.saveError = null
        this.saveErrorFields = []
        try {
          await this.service.saveSettings(body)
          this.committed++
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('documentSettings.settingsSaved'),
            color: 'success',
          })
          if (display) {
            EventBus.$emit('documentSettingsSaved')
          }
          await this.load()
        } catch (error) {
          // Two refusals reach a user who did nothing wrong, and the generic message misdirects on
          // both. A key added elsewhere since the load is missing from the payload, which the api
          // reads as a removal: only a reload clears it. And a key still selected as a column cannot
          // be deactivated: the api names it, so the message can too.
          const { message, details } = error.response?.data || {}
          const refusals = {
            custom_field_removal_not_allowed: 'staleRegistry',
            custom_field_in_use: 'fieldInUse',
            // The dialog refuses a reserved key, so this only fires if the two lists of standard
            // columns drift apart — and then the message has to name the key rather than send the
            // admin looking through the whole registry.
            custom_field_reserved_key: 'fieldReserved',
          }
          this.saveError = error.response?.status === 409 ? 'conflict' : refusals[message] || 'saveError'
          this.saveErrorFields = details?.values || []
        } finally {
          this.saving = false
        }
      },
      // A response is only worth writing if this view still exists and nothing newer was asked for.
      current(request) {
        return !this.destroyed && request === this.request
      },
    },
  }
</script>
